import React, { createContext, useContext, useState, useEffect } from 'react';
import { defaultTheme, themeKeyToCssVar } from '../config/theme';

const ThemeContext = createContext();

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext deve ser usado dentro de ThemeProvider');
  }
  return context;
};

// "#1E5AA8" → "30 90 168". Aceita também "1E5AA8" sem #.
// Retorna separado por ESPAÇOS pq o Tailwind gera
// `rgb(var(--color-*-rgb) / <alpha-value>)` — sintaxe moderna que só
// aceita separadores com espaço (Firefox é strict, Chrome tolera vírgula).
function hexToRgb(hex) {
  if (!hex) return null;
  const normalized = hex.replace('#', '');
  if (normalized.length !== 6) return null;
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return null;
  return `${r} ${g} ${b}`;
}

function applyTheme(theme) {
  const root = document.documentElement;
  Object.entries(themeKeyToCssVar).forEach(([key, varName]) => {
    const hex = theme[key];
    if (!hex) return;
    root.style.setProperty(`--color-${varName}`, hex);
    const rgb = hexToRgb(hex);
    if (rgb) root.style.setProperty(`--color-${varName}-rgb`, rgb);
  });
}

const FAVICON_LINK_ID = 'reviter-dynamic-favicon';
const FONT_LINK_ID = 'reviter-dynamic-font';
const REVITER_FALLBACK_FAVICON = '/reviter.png';

// Atualiza/instala um <link rel="icon"> no <head> apontando pra `url`.
// Quando `url` é null/vazio cai no favicon Reviter como fallback.
function applyFavicon(url) {
  if (typeof document === 'undefined') return;
  let link = document.getElementById(FAVICON_LINK_ID);
  if (!link) {
    link = document.createElement('link');
    link.id = FAVICON_LINK_ID;
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = url || REVITER_FALLBACK_FAVICON;
}

// Sincroniza a barra de navegação mobile (Chrome Android, Safari iOS) com a
// cor primária do tema da loja. Atualiza também msapplication-TileColor
// (Windows tiles) e apple-mobile-web-app-status-bar-style.
function applyMetaThemeColor(color) {
  if (typeof document === 'undefined' || !color) return;
  const targets = [
    'meta[name="theme-color"]',
    'meta[name="msapplication-TileColor"]',
    'meta[name="apple-mobile-web-app-status-bar-style"]'
  ];
  targets.forEach((selector) => {
    const el = document.querySelector(selector);
    if (el) el.setAttribute('content', color);
  });
}

// Carrega Google Fonts dinamicamente e aplica `font-family` no :root.
// `font` aceita só o nome da família ("Poppins", "Inter", etc). Espaços
// são tratados na URL — ex: "Open Sans" vira "Open+Sans".
function applyFont(font) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (!font) {
    root.style.removeProperty('--font-family');
    return;
  }
  const family = String(font).trim();
  const familyParam = family.replace(/\s+/g, '+');
  const href = `https://fonts.googleapis.com/css2?family=${familyParam}:wght@400;500;600;700&display=swap`;

  let link = document.getElementById(FONT_LINK_ID);
  if (!link) {
    link = document.createElement('link');
    link.id = FONT_LINK_ID;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }
  if (link.href !== href) link.href = href;

  root.style.setProperty('--font-family', `'${family}', system-ui, sans-serif`);
}

// Stores no plano CUSTOM rodam com front-end customizado — o tema do
// backoffice é ignorado (Seção 2.4 do doc).
function shouldApplyTheme(planType) {
  return planType !== 'CUSTOM';
}

export const ThemeProvider = ({ children, supplierTheme = null, planType = 'DEFAULT' }) => {
  const [theme, setTheme] = useState(supplierTheme || defaultTheme);

  useEffect(() => {
    if (!shouldApplyTheme(planType)) return;
    const merged = { ...defaultTheme, ...(theme || {}) };
    applyTheme(merged);
    applyFavicon(theme?.favicon);
    applyFont(theme?.font);
    applyMetaThemeColor(merged.primary);
  }, [theme, planType]);

  useEffect(() => {
    if (supplierTheme) setTheme(supplierTheme);
  }, [supplierTheme]);

  const updateTheme = (newTheme) => setTheme(newTheme);

  return (
    <ThemeContext.Provider value={{ theme, updateTheme, planType }}>
      <div className="theme-transition" style={{ fontFamily: 'var(--font-family, inherit)' }}>{children}</div>
    </ThemeContext.Provider>
  );
};
